import {
  DIMENSION_UNIT_SYMBOL,
  FP_CUSTOM_SOURCE_PREFIX,
  WEIGHT_UNIT_ABBREVIATION,
  FP_LINE_FIELD_SOURCES,
  type FpLineFieldSource,
  type PublicQuote,
  type PublicQuoteAsset,
  type PublicQuoteItemLine,
  type PublicQuoteLine,
  type PublicQuoteQuantityBreak,
} from "@forgepoint/shared";
import {
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
} from "@/lib/quoteCurrency";
import {
  lookupCustomField,
  normalizeCustomFields,
} from "./customFieldResolution";
import type { LineScopeValue } from "./lineScopeContext";
import { splitFieldTokens } from "./quoteFields";

/** The rendered form of a resolved line field. */
export type LineFieldFormat = "text" | "image";

/** A resolved line-field binding: its render format and value (an image URL for `image`). */
export interface ResolvedLineField {
  format: LineFieldFormat;
  value: string;
}

/** File extensions treated as renderable images for `item-image`. */
const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".tif",
  ".tiff",
];

/** The data a line-field resolver reads: the quote and the repeater scope. */
interface LineFieldContext {
  quote: PublicQuote;
  scope: LineScopeValue;
}

/** One line-field source's render format and resolver. */
interface LineFieldDefinition {
  format: LineFieldFormat;
  resolve: (context: LineFieldContext) => string;
}

/** Line-field definitions keyed by the `fp-line-field` source vocabulary. */
const LINE_FIELD_DEFINITIONS: Record<FpLineFieldSource, LineFieldDefinition> = {
  number: {
    format: "text",
    resolve: ({ scope }) => scope.itemNumber ?? "",
  },
  description: {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "DISCOUNT"
        ? scope.line.description || "Discount"
        : scope.line.description,
  },
  "item-name": {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.name ?? "") : "",
  },
  "item-description": {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.description ?? "") : "",
  },
  "item-image": {
    format: "image",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM"
        ? (imagePreviewUrl(scope.line.item?.primaryFileAsset) ?? "")
        : "",
  },
  quantity: {
    format: "text",
    resolve: ({ scope }) => currentBreak(scope)?.quantity ?? "",
  },
  "unit-price": {
    format: "text",
    resolve: ({ quote, scope }) => {
      const quantityBreak = currentBreak(scope);
      return quantityBreak ? formatQuoteUnitPrice(quantityBreak, quote) : "";
    },
  },
  amount: {
    format: "text",
    resolve: resolveAmount,
  },
  weight: {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.weight ?? "") : "",
  },
  "weight-unit": {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.weightUnit ?? "") : "",
  },
  "weight-unit-symbol": {
    format: "text",
    resolve: ({ scope }) => {
      const weightUnit =
        scope.line.type === "ITEM" ? scope.line.item?.weightUnit : null;
      return weightUnit ? WEIGHT_UNIT_ABBREVIATION[weightUnit] : "";
    },
  },
  length: {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.length ?? "") : "",
  },
  width: {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.width ?? "") : "",
  },
  height: {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.height ?? "") : "",
  },
  "dimension-unit": {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.dimensionUnit ?? "") : "",
  },
  "dimension-unit-symbol": {
    format: "text",
    resolve: ({ scope }) => {
      const dimensionUnit =
        scope.line.type === "ITEM" ? scope.line.item?.dimensionUnit : null;
      return dimensionUnit ? DIMENSION_UNIT_SYMBOL[dimensionUnit] : "";
    },
  },
  unit: {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.unit ?? "") : "",
  },
  "item-revision": {
    format: "text",
    resolve: ({ scope }) =>
      scope.line.type === "ITEM" ? (scope.line.item?.revision ?? "") : "",
  },
};

/**
 * Turns a line-field source key into a picker label ("unit-price" -> "Unit price").
 * @param source - The `fp-line-field` source attribute value
 * @returns The human-readable label
 */
export function lineFieldLabel(source: string): string {
  return capitalizeFirst(sourceWords(source));
}

/** A pickable line-scope source: its `source` value and its friendly label. */
export interface LineFieldOption {
  source: string;
  label: string;
}

/** Every standard line-scope source with its picker label, in vocabulary order. */
export const LINE_FIELD_OPTIONS: readonly LineFieldOption[] =
  FP_LINE_FIELD_SOURCES.map((source) => ({
    source,
    label: lineFieldLabel(source),
  }));

/**
 * Builds the pickable options for the org's line custom fields, whose names
 * double as their labels.
 * @param customFieldNames - The org's line item custom field names
 * @returns One option per custom field, carrying a `custom:<name>` source
 */
export function lineCustomFieldOptions(
  customFieldNames: readonly string[],
): LineFieldOption[] {
  return customFieldNames.map((name) => ({
    source: `${FP_CUSTOM_SOURCE_PREFIX}${name}`,
    label: name,
  }));
}

/**
 * The pickable `number-source` values: the org's line custom fields, plus an
 * authored source the org no longer configures so selecting a block never
 * silently rewrites the author's value.
 * @param customFieldNames - The org's line item custom field names
 * @param authoredSource - The block's authored `number-source`, or "" when unset
 * @returns One option per pickable number source
 */
export function itemNumberingOptions(
  customFieldNames: readonly string[],
  authoredSource: string,
): LineFieldOption[] {
  const options = lineCustomFieldOptions(customFieldNames);
  if (
    authoredSource === "" ||
    options.some((option) => option.source === authoredSource)
  ) {
    return options;
  }
  return [
    ...options,
    {
      source: authoredSource,
      label: authoredSource.slice(FP_CUSTOM_SOURCE_PREFIX.length),
    },
  ];
}

/** Row-type words prefixed onto chip labels outside item rows. */
const LINE_TYPE_CHIP_PREFIX: Record<
  Exclude<PublicQuoteLine["type"], "ITEM">,
  string
> = {
  SECTION: "Section",
  NOTE: "Note",
  DISCOUNT: "Discount",
};

/**
 * The chip label for an empty line field. Rows other than items name their row
 * type first, so the same source bound on a section, note, and item row stays
 * tellable apart while authoring.
 * @param source - The `fp-line-field` source attribute value
 * @param lineType - The type of the line the chip renders inside
 * @returns The label to show inside the chip
 */
export function emptyLineFieldLabel(
  source: string,
  lineType: PublicQuoteLine["type"],
): string {
  const words = sourceWords(source);
  if (lineType === "ITEM") {
    return capitalizeFirst(words);
  }
  return `${LINE_TYPE_CHIP_PREFIX[lineType]} ${words}`;
}

/**
 * Resolves an `fp-line-field` source against the current repeater scope.
 * @param quote - The public quote backing the document
 * @param scope - The enclosing repeater scope
 * @param source - The source key from the document, or null when unset
 * @returns The resolved field, or null when the key is missing or unknown
 */
export function resolveLineField(
  quote: PublicQuote,
  scope: LineScopeValue,
  source: string | null,
): ResolvedLineField | null {
  if (!source) {
    return null;
  }
  if (source.startsWith(FP_CUSTOM_SOURCE_PREFIX)) {
    const customFields =
      scope.line.type === "ITEM"
        ? normalizeCustomFields(scope.line.customFields)
        : {};
    return {
      format: "text",
      value:
        lookupCustomField(
          customFields,
          source.slice(FP_CUSTOM_SOURCE_PREFIX.length),
        ) ?? "",
    };
  }
  if (!isFpLineFieldSource(source)) {
    return null;
  }
  const definition = LINE_FIELD_DEFINITIONS[source];
  return {
    format: definition.format,
    value: definition.resolve({ quote, scope }),
  };
}

/**
 * Interpolates `{{ source }}` line-field tokens in a column's literal text, so
 * a `suffix="{{ weight-unit }}"` prints the unit against its bound value. An
 * unknown or empty token resolves to nothing, matching quote-scope tokens.
 * @param quote - The public quote backing the document
 * @param scope - The enclosing repeater scope
 * @param text - The authored literal, or null when absent
 * @returns The text with each token replaced by its resolved value
 */
export function interpolateLineTokens(
  quote: PublicQuote,
  scope: LineScopeValue,
  text: string | null,
): string | null {
  if (text === null || !text.includes("{{")) {
    return text;
  }
  return splitFieldTokens(text)
    .map((segment) =>
      "text" in segment
        ? segment.text
        : (resolveLineField(quote, scope, segment.source)?.value ?? ""),
    )
    .join("");
}

/**
 * Assigns display item numbers across the whole quote in quote-data order. When
 * `numberFieldName` is set, a line's number comes from that line custom field,
 * falling back to its sequential position; otherwise the sequential number.
 * @param quote - The public quote backing the document
 * @param numberFieldName - The line custom-field name driving numbers, or null
 * @returns A map of line id to display item number
 */
export function buildItemNumbers(
  quote: PublicQuote,
  numberFieldName: string | null,
): Map<string, string> {
  const itemNumbers = new Map<string, string>();
  quote.lines
    .filter((line): line is PublicQuoteItemLine => line.type === "ITEM")
    .forEach((line, index) => {
      const override =
        numberFieldName === null
          ? null
          : lookupCustomField(
              normalizeCustomFields(line.customFields),
              numberFieldName,
            );
      itemNumbers.set(line.id, override ?? `${index + 1}`);
    });
  return itemNumbers;
}

/**
 * Parses a `number-source` attribute into the line custom-field
 * name driving item numbers, or null when absent or not a `custom:` source.
 * @param value - The raw `number-source` attribute value, or null when absent
 * @returns The custom-field name, or null when absent or not a `custom:` source
 */
export function parseNumberSourceAttribute(
  value: string | null,
): string | null {
  if (value === null || !value.startsWith(FP_CUSTOM_SOURCE_PREFIX)) {
    return null;
  }
  const fieldName = value.slice(FP_CUSTOM_SOURCE_PREFIX.length).trim();
  return fieldName === "" ? null : fieldName;
}

/**
 * The spaced-out words behind a source key: a custom field's own name, or the
 * vocabulary key with its dashes removed.
 * @param source - The `fp-line-field` source attribute value
 * @returns The label words, uncapitalized
 */
function sourceWords(source: string): string {
  if (source.startsWith(FP_CUSTOM_SOURCE_PREFIX)) {
    return source.slice(FP_CUSTOM_SOURCE_PREFIX.length);
  }
  return source.replace(/-/g, " ");
}

/**
 * Uppercases the first character of a label.
 * @param label - The label to capitalize
 * @returns The label with its first character uppercased
 */
function capitalizeFirst(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Whether a raw source key is one of the line-scope field sources.
 * @param source - The raw source key to test
 * @returns True when the key is a known line-scope field source
 */
function isFpLineFieldSource(source: string): source is FpLineFieldSource {
  return FP_LINE_FIELD_SOURCES.some((candidate) => candidate === source);
}

/**
 * The quantity break the scope binds: the current `fp-breaks` stamp, else the
 * item line's first break.
 * @param scope - The enclosing repeater scope
 * @returns The bound quantity break, or null on non-item lines
 */
function currentBreak(scope: LineScopeValue): PublicQuoteQuantityBreak | null {
  if (scope.quantityBreak) {
    return scope.quantityBreak;
  }
  return scope.line.type === "ITEM"
    ? (scope.line.quantityBreaks[0] ?? null)
    : null;
}

/**
 * Resolves the `amount` source: the current break's line total for an item
 * line, or the negated server-computed discount amount for a discount line.
 * @param context - The quote and repeater scope
 * @returns The formatted amount, "-" when unpriced, or "" on other line types
 */
function resolveAmount({ quote, scope }: LineFieldContext): string {
  if (scope.line.type === "ITEM") {
    return formatQuoteLineTotal(currentBreak(scope), quote);
  }
  if (scope.line.type === "DISCOUNT") {
    if (scope.line.amount === null) {
      return "-";
    }
    return `-${formatQuoteLineTotal(scope.line, quote)}`;
  }
  return "";
}

/**
 * The web-viewable image URL of an item's primary asset, or null when the
 * asset is absent or not an image.
 * @param asset - The item's primary file asset, or null/undefined when absent
 * @returns The preview image URL, or null
 */
function imagePreviewUrl(
  asset: PublicQuoteAsset | null | undefined,
): string | null {
  if (!asset) {
    return null;
  }
  const fileName = asset.webViewFileName ?? asset.fileName;
  const fileType = asset.webViewFileType ?? asset.fileType;
  const isImage =
    (fileType ? fileType.startsWith("image/") : false) ||
    IMAGE_EXTENSIONS.some((extension) =>
      fileName.toLowerCase().endsWith(extension),
    );
  if (!isImage) {
    return null;
  }
  return asset.webViewExternalUrl ?? asset.externalUrl ?? null;
}
